import { useMemo, useState } from "react";
import { useQueries } from "@tanstack/react-query";

import { queryKeys } from "@nasam/kernel";
import type { DeviceListItem, DevicesRepository } from "../../../contracts";
import { useDebouncedValue } from "../../../shared/application/useDebouncedValue";

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Active devices for the Send GPRS Command dialog's IMEI picker, searched by
 * IMEI and scoped to the chosen companies. Unlike `useDeviceOptions` (one
 * account at a time) this runs one `GET /devices` per selected company and
 * merges them, and keeps the full rows so the picker can show each device's
 * asset name. `accountIds` is `undefined` for a client user, whose own
 * account the backend already scopes to.
 */
export function useGprsDeviceOptions(
  repository: DevicesRepository | undefined,
  accountIds: readonly string[] | undefined,
) {
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebouncedValue(searchTerm, SEARCH_DEBOUNCE_MS);
  const imei = debouncedSearch.trim() === "" ? undefined : debouncedSearch.trim();

  // A client user gets one unscoped query; staff get one per company.
  const scopes: readonly (string | undefined)[] =
    accountIds === undefined ? [undefined] : accountIds;

  const queries = useQueries({
    queries: scopes.map((accountId) => ({
      queryKey: queryKeys.devices.list({
        limit: PAGE_SIZE,
        status: "ACTIVE",
        sortBy: "imei",
        accountId,
        imei,
      }),
      queryFn: () =>
        repository!.list({
          limit: PAGE_SIZE,
          status: "ACTIVE",
          sort: "asc",
          sortBy: "imei",
          ...(accountId === undefined ? {} : { accountId }),
          ...(imei === undefined ? {} : { imei }),
        }),
      enabled: repository !== undefined,
    })),
  });

  // Joined ids are the stable dependency -- `queries` is a new array every render.
  const dataKey = queries
    .map((query) => query.data?.data.map((device) => device.id).join(",") ?? "")
    .join("|");
  const devices = useMemo<readonly DeviceListItem[]>(
    () => queries.flatMap((query) => query.data?.data ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataKey],
  );

  return {
    devices,
    searchValue: searchTerm,
    onSearchChange: setSearchTerm,
    isLoading: queries.some((query) => query.isLoading),
  };
}
